import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react'
import {
  ArrowRightOnRectangleIcon,
  ComputerDesktopIcon,
  MoonIcon,
  ShieldCheckIcon,
  SunIcon,
  UserCircleIcon,
} from '@heroicons/react/24/outline'
import clsx from 'clsx'

import { useAuth } from '../../hooks/useAuth'
import { useTheme, THEMES } from '../../hooks/useTheme'
import { getAccountNavigation, getMenuNavigation } from './navigation'

const THEME_ICONS = { light: SunIcon, dark: MoonIcon, system: ComputerDesktopIcon }

const itemClass =
  'flex min-h-[44px] w-full items-center gap-2 px-3 py-2.5 text-left text-sm text-gray-700 data-[focus]:bg-gray-100 data-[focus]:text-gray-900'

/**
 * The phone's user menu — VH-297 (HP-38, LifeBrain's UserMenu)
 *
 * Replaces the ☰ drawer. One icon at the top right opens: the user's name,
 * every destination the bottom bar has no room for (read from the one
 * navigation list, never a second hand-kept one), then the account part —
 * Profile, Security, the theme, Admin for super users — and Log out last.
 *
 * Phone only: from 768px the header keeps its theme and log-out buttons and
 * the rail or sidebar lists every destination.
 */
export default function UserMenu() {
  const { t } = useTranslation()
  const { user, logout } = useAuth()
  const { preference, setTheme } = useTheme()
  const name = user?.username || user?.email
  const destinations = getMenuNavigation(t, user)
  const account = getAccountNavigation(t, user)
  const [profile, ...admin] = account

  const link = (item) => (
    <MenuItem key={item.key}>
      <Link to={item.href} className={itemClass}>
        <item.icon className="h-5 w-5 shrink-0 text-gray-500" />
        {item.name}
      </Link>
    </MenuItem>
  )

  return (
    <Menu as="div" className="relative md:hidden">
      <MenuButton
        aria-label={name ? t('nav.userMenuNamed', { name }) : t('nav.userMenu')}
        className="flex h-11 w-11 items-center justify-center rounded-md text-gray-600 hover:bg-gray-100 hover:text-gray-900 data-[open]:bg-gray-100"
      >
        <UserCircleIcon className="h-6 w-6" />
      </MenuButton>
      <MenuItems
        className="absolute right-0 top-full z-50 mt-1 max-h-[calc(100dvh-5rem)] w-60 max-w-[calc(100vw-2rem)] overflow-y-auto rounded-md bg-surface-container py-1 shadow-lg ring-1 ring-outline-variant focus:outline-none"
      >
        {/* The name, because the phone's header does not show it. */}
        <p className="truncate border-b border-outline-variant px-3 py-2 text-sm font-semibold text-gray-900">
          {name || t('nav.userMenu')}
        </p>

        {destinations.map(link)}

        <div className="border-t border-outline-variant" />
        {profile && link(profile)}
        {link({ key: 'security', name: t('nav.security'), href: '/security', icon: ShieldCheckIcon })}

        <div className="flex items-center gap-1 px-3 py-2" role="group" aria-label={t('theme.switchTheme')}>
          {THEMES.map((option) => {
            const Icon = THEME_ICONS[option]
            return (
              <MenuItem key={option}>
                <button
                  type="button"
                  onClick={() => setTheme(option)}
                  aria-pressed={preference === option}
                  aria-label={t(`theme.${option}`)}
                  title={t(`theme.${option}`)}
                  className={clsx(
                    'flex h-11 flex-1 items-center justify-center rounded-md data-[focus]:bg-gray-100',
                    preference === option ? 'bg-gray-100 text-primary-600' : 'text-gray-500'
                  )}
                >
                  <Icon className="h-5 w-5" />
                </button>
              </MenuItem>
            )
          })}
        </div>

        {admin.map(link)}

        <MenuItem>
          <button type="button" onClick={logout} className={clsx(itemClass, 'border-t border-outline-variant')}>
            <ArrowRightOnRectangleIcon className="h-5 w-5 shrink-0 text-gray-500" />
            {t('common.logout')}
          </button>
        </MenuItem>
      </MenuItems>
    </Menu>
  )
}
